/**
 * Goggles connectivity across a recorded day. A ski day spans several app
 * sessions, so deriveGogglesConnectivityTimeline runs over the events of every
 * session overlapping the recording, and these helpers place the result on the
 * playback timeline.
 */

const CRITICAL_EVENT_NAMES = ['GogglesForgotten', 'BleWriteAckWedged', 'GogglesOTAFailed'];

const toMs = (time) => new Date(time).getTime();

/** First and last moment covered by the recording or any of its sessions, in ms. */
export const dayConnectivitySpan = (sessions, trackStartMs, trackEndMs) => {
  const times = sessions.flatMap((s) => [s.startTime, s.endTime]).filter(Boolean).map(toMs);
  return { startMs: Math.min(trackStartMs, ...times), endMs: Math.max(trackEndMs, ...times) };
};

/** A start and end in ms → left and width as % of the track, or null when outside it. */
export const toTrackSpan = (startMs, endMs, trackStartMs, trackEndMs) => {
  if (endMs < trackStartMs || startMs > trackEndMs) return null;
  const length = Math.max(1, trackEndMs - trackStartMs);
  const left = ((Math.max(startMs, trackStartMs) - trackStartMs) / length) * 100;
  const right = ((Math.min(endMs, trackEndMs) - trackStartMs) / length) * 100;
  return { left, width: right - left, startMs, endMs };
};

/**
 * Everything the playback strip draws, clipped to the recording.
 *
 * @param {Object} data - from deriveGogglesConnectivityTimeline
 * @param {number} trackStartMs - First recorded point
 * @param {number} trackEndMs - Last recorded point
 * @returns {{ connections: Array, failures: Array, otaAttempts: Array, criticalEvents: Array }}
 */
export const buildConnectivityStrip = (data, trackStartMs, trackEndMs) => {
  const place = (items, getStart, getEnd) => items
    .map((item) => {
      const span = toTrackSpan(getStart(item), getEnd(item), trackStartMs, trackEndMs);
      return span && { ...span, item };
    })
    .filter(Boolean);

  return {
    connections: place(data.gogglesConnectedSessions, (c) => c.startTime, (c) => c.endTime ?? trackEndMs),
    failures: place(data.gogglesConnectFailureEpisodes, (ep) => ep.startTime, (ep) => ep.endTime),
    otaAttempts: place(data.gogglesOtaAttempts, (a) => a.startTime, (a) => a.endTime ?? a.startTime),
    criticalEvents: place(
      data.events.filter((e) => CRITICAL_EVENT_NAMES.includes(e.eventName)),
      (e) => toMs(e.timestamp),
      (e) => toMs(e.timestamp)
    )
  };
};

/** 'connected', 'failing' or 'disconnected' at a moment of the day. */
export const connectionStateAt = (data, ms) => {
  const inside = (start, end) => start <= ms && (end == null || ms <= end);
  if (data.gogglesConnectedSessions.some((c) => inside(c.startTime, c.endTime))) return 'connected';
  if (data.gogglesConnectFailureEpisodes.some((ep) => inside(ep.startTime, ep.endTime))) return 'failing';
  return 'disconnected';
};
